import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Crown, ArrowRight } from 'lucide-react';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState(null);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      handleSubscriptionSuccess();
    } else {
      setError('No session ID provided');
      setProcessing(false);
    }
  }, [sessionId]);

  const handleSubscriptionSuccess = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/subscriptions/success`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sessionId })
      });
      
      if (response.ok) {
        // Success - subscription activated
        setProcessing(false);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to process subscription');
      }
    } catch (error) {
      console.error('Error processing subscription:', error);
      setError(error.message);
      setProcessing(false);
    }
  };

  if (processing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Processing Your Subscription...</h2>
          <p className="text-gray-600">Please wait while we activate your Pro features.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Crown className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Subscription Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/subscription')}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
            >
              Back to Subscription
            </button>
            <Link
              to="/contact"
              className="block text-purple-600 hover:text-purple-700 text-sm underline"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to EDX Red Pro!</h2>
        <p className="text-gray-600 mb-6">
          Your subscription has been successfully activated. You now have unlimited access to Lexi, our AI assistant.
        </p>
        
        <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-center mb-2">
            <Crown className="w-5 h-5 text-purple-600 mr-2" />
            <span className="font-semibold text-purple-800">Pro Features Unlocked</span>
          </div>
          <ul className="text-sm text-purple-700 space-y-1">
            <li>✨ Unlimited AI chat with Lexi</li>
          </ul>
        </div>
        
        <div className="space-y-3">
          <Link
            to="/chat"
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center group"
          >
            Try Lexi Now
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          
          <button
            onClick={() => navigate('/subscription')}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-medium transition-colors"
          >
            Manage Subscription
          </button>
        </div>
        
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Questions? <Link to="/help" className="text-purple-600 hover:underline">Contact our support team</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;